import CollapsibleSection from '@/global/components/CollapsibleSection'
import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import { useCompetencia } from '@/global/components/Selects/SelectCompetencia'
import Grid from '@/global/components/ui/Grid'
import { useAuth } from '@/hooks'
import { abonos, createAbonoRecorrenteSchema } from '@/service/api'
import { CategoriaEnum } from '@acme/auth'
import dayjs from 'dayjs'
import {
    FormDatePicker,
    FormField,
    FormFieldAutocomplete,
    FormFieldSelect,
    FormFile,
} from 'global/components/Form'
import IconeAjuda from 'global/components/IconeAjuda'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { z } from 'zod'
import { buildAbono } from '../../Ausencias/modals/AdicionarAbono'
import CalendarioAbonos from '../atoms/CalendarioAbonos'
import RepeticaoAbonoAccordion from '../atoms/RepeticaoAbonoAccordion'
import TabFuncionariosForm from '../atoms/TabsFuncionarios'

export default function FormNovoAbono() {
    const {
        decoded: { categoria },
    } = useAuth()
    const { watch, setValue } =
        useFormContext<z.infer<typeof createAbonoRecorrenteSchema>>()
    const {
        data: dataAbono,
        tipoRepeticao,
        repeticaoMensalOpcao,
        every,
        dataFinal,
        funcionarioId,
        ...rest
    } = watch()
    const [mes, setMes] = useCompetencia()

    useEffect(() => {
        if (dataAbono) setMes(dayjs(dataAbono).format('YYYY-MM'))
    }, [dataAbono])

    const MemoizedFuncionariosForm = useMemo(
        () => (
            <TabFuncionariosForm
                funcionarioIds={funcionarioId}
                onChange={v => setValue('funcionarioId', v)}
                collapsible
            />
        ),
        [funcionarioId],
    )

    const datas = useMemo(
        () =>
            buildAbono(
                {
                    ...rest,
                    justificativa: rest.justificativa || undefined,
                    data: dataAbono,
                },
                {
                    tipoRepeticao,
                    repeticaoMensalOpcao,
                    every,
                    dataFinal,
                },
            ).data,
        [dataAbono, tipoRepeticao, repeticaoMensalOpcao, every, dataFinal],
    )

    const MemoizedRepeticao = useMemo(
        () => (
            <Grid container>
                <Grid item xs={6}>
                    <FormDatePicker
                        name="dataFinal"
                        label="Fim da repetição"
                        type="date"
                        autoComplete="off"
                    />
                </Grid>
                <Grid item xs={12}>
                    <RepeticaoAbonoAccordion />
                </Grid>
                <Grid item xs={12}>
                    <HorizontalDivider className="my-4 select-none">
                        Prévia do lançamento (apenas visualização)
                    </HorizontalDivider>
                </Grid>
                <Grid item xs={12}>
                    <CalendarioAbonos
                        key={mes}
                        value={mes}
                        onChange={setMes}
                        datas={datas}
                    />
                </Grid>
            </Grid>
        ),
        [mes, datas],
    )

    return [
        MemoizedFuncionariosForm,
        <Grid container key="form">
            <Grid item xs={12}>
                <HorizontalDivider className="my-4 select-none">
                    Detalhes do abono
                </HorizontalDivider>
            </Grid>
            <Grid item xs={6} sm={4}>
                <FormDatePicker
                    name="data"
                    label="Data de abono"
                    type="date"
                    required
                />
            </Grid>
            <Grid item xs={6} sm={3}>
                <FormField
                    name="quantidadeHoras"
                    label="Horas abonadas"
                    required
                    placeholder="HH:mm"
                    options={{
                        blocks: [2, 2],
                        delimiters: [':'],
                        numericOnly: true,
                    }}
                />
            </Grid>
            <Grid item xs={12} sm={5}>
                <FormFieldSelect
                    label="Tipo de abono"
                    name="tipo"
                    options={[
                        { label: 'Normal', value: 'NORMAL' },
                        {
                            label: 'Saúde Ocupacional',
                            value: 'SAUDE_OCUPACIONAL',
                        },
                    ]}
                    endAdornment={
                        <IconeAjuda>
                            O abono de Saúde Ocupacional é concedido para
                            atestados médicos e afins.
                            <br />
                            <br />
                            Para o relatório de absenteísmo: um abono normal
                            contabiliza ausências justificadas, já o abono de
                            saúde ocupacional é considerado como horas
                            trabalhadas e não soma faltas no dia.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item xs={12}>
                <HorizontalDivider className="my-4 select-none">
                    Motivos do abono (preencha pelo menos um campo)
                </HorizontalDivider>
            </Grid>
            <Grid item xs={12}>
                <AutocompleteDescricaoAbono />
            </Grid>
            <Grid item xs={12}>
                <FormFieldJustificativaAbono />
            </Grid>
            <Grid item xs={12}>
                <FormFile label="Anexo" name="fileUrl" clearable />
            </Grid>
        </Grid>,
        categoria !== CategoriaEnum.Funcionario && (
            <CollapsibleSection title="Recorrência (opcional)" key="repeticao">
                {MemoizedRepeticao}
            </CollapsibleSection>
        ),
    ]
}

export function FormFieldJustificativaAbono({
    name = 'justificativa',
}: {
    name?: string
}) {
    return (
        <FormField
            label="Justificativa"
            name={name}
            multiline
            max={190}
            endAdornment={
                <IconeAjuda>
                    Justificativa do abono, é uma explicação sobre o motivo do
                    abono. (Máximo de 190 caracteres)
                    <br />
                    <br />A justificativa é opcional, mas é recomendada para
                    fornecer contexto adicional sobre o abono. Ela não é exibida
                    no espelho do ponto e só pode ser visualizada ao editar ou
                    ver detalhes do abono.
                </IconeAjuda>
            }
        />
    )
}

export function AutocompleteDescricaoAbono({
    name = 'descricao',
}: {
    name?: string
}) {
    const { data = [] } = abonos.useFetchById<string[]>('descricoes', {
        enabled: true,
        refetchOnWindowFocus: false,
    })
    const [digitando, setDigitando] = useState<string>('')

    const handleInputChange = (inputValue: string) => {
        setDigitando(inputValue.trim())
    }

    const criarNovaDescricao = () => {
        if (digitando && !data.includes(digitando)) {
            abonos.setQueryData<string[]>(
                { id: 'descricoes' },
                (prev: string[] | undefined) =>
                    prev ? [digitando, ...prev] : [digitando],
            )
        }
        setDigitando('')
    }

    return (
        <FormFieldAutocomplete
            name={name}
            label="Descrição do abono"
            endAdornment={
                <IconeAjuda>
                    Descrição do abono, pode ser uma justificativa curta ou
                    motivo específico. (Máximo de 30 caracteres)
                    <br />
                    <br />
                    Se a descrição não existir, você pode começar a digitar e
                    criar uma nova clicando no botão "Criar nova descrição".
                    <br />
                    <br />
                    As descrições são úteis para categorizar e identificar
                    rapidamente o motivo do abono, elas são exibidas no espelho
                    do ponto.
                </IconeAjuda>
            }
            options={data}
            onInputChange={handleInputChange}
            searchPlaceholder="Buscar ou digitar para criar uma descrição"
            creatableOption={
                digitando.length > 0 && !data.includes(digitando)
                    ? {
                          label: `Criar nova descrição: "${digitando}"`,
                          value: digitando,
                          onClick: criarNovaDescricao,
                      }
                    : undefined
            }
            noOptionsText="Nenhuma descrição encontrada, comece a digitar para criar uma nova descrição."
        />
    )
}
